import { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';

export const useGetFacilities = (options = {}) => {
  const {
    search = '',
    facility = true,
    organizationId = null,
    allColumns = false,
    enabled = true,
  } = options;

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      if (allColumns) {
        response = await FacilitiesApi.getFacilities({
          facility,
          search,
          organizationId,
        });
        setFacilities(response.data);
      } else {
        const facilities = await FacilitiesApi.getFacilitiesForForms({
          organizationId,
        });
        setFacilities(facilities);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch facilities');
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  }, [search, facility, organizationId, allColumns]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (search) {
      const debouncedFetch = debounce(fetchFacilities, 300);
      debouncedFetch();
      return () => debouncedFetch.cancel();
    } else {
      fetchFacilities();
    }
  }, [fetchFacilities, enabled, search]);

  const refetch = () => {
    fetchFacilities();
  };

  const refresh = () => {
    fetchFacilities();
  };

  return {
    facilities,
    loading,
    error,
    refetch,
    refresh,
  };
};
